<template>
	<Tooltip
		:placement="position"
		:shown="computedShown"
		:triggers="[]"
		:distance="10"
		:disabled="!hasContent"
		:auto-hide="true"
		:no-auto-focus="true"
		theme="tooltip"
		:popper-class="popperClasses"
		@show="onShow"
		@hide="onHide"
	>
		<slot />

		<template #popper>
			<slot name="content">
				{{ contentFormatted }}
			</slot>
		</template>
	</Tooltip>
</template>

<script>
import { Tooltip } from 'floating-vue'

export default {
	name: 'BaseTooltip',
	components: {
		Tooltip
	},
	props: {
		content: {
			type: [String, Array, Boolean],
			default: null
		},
		position: {
			type: String,
			default: 'top'
		},
		white: {
			type: [Boolean, String],
			default: false
		},
		wrap: {
			type: Boolean,
			default: false
		}
	},
	data () {
		return {
			isVisible: false
		}
	},
	computed: {
		contentFormatted () {
			if (Array.isArray(this.content)) {
				return this.content.join('\n')
			}
			return this.content
		},
		hasContent () {
			return this.$slots.content || (this.contentFormatted && this.contentFormatted.length)
		},
		computedShown () {
			return this.isVisible && this.hasContent ? true : undefined
		},
		popperClasses () {
			const classes = []
			if (this.white) {
				classes.push('white-tooltip')
			}
			if (this.wrap) {
				classes.push('tooltip-wrap')
			}
			return classes.join(' ')
		}
	},

	mounted () {
		this.$el.addEventListener('mouseenter', this.showTooltip)
		this.$el.addEventListener('mouseleave', this.hideTooltip)
	},

	beforeDestroy () {
		this.$el.removeEventListener('mouseenter', this.showTooltip)
		this.$el.removeEventListener('mouseleave', this.hideTooltip)
	},
	methods: {
		blurActiveTooltip () {
			const activeElement = document.activeElement
			if (!activeElement || typeof activeElement.blur !== 'function') {
				return
			}
			if (activeElement.classList?.contains('v-popper__popper') || activeElement.closest?.('.v-popper__popper')) {
				activeElement.blur()
			}
		},
		showTooltip () {
			this.isVisible = true
		},
		hideTooltip (event) {
			if (event?.relatedTarget && this.$el?.contains(event.relatedTarget)) {
				return
			}
			this.blurActiveTooltip()
			this.isVisible = false
		},
		onShow () {
			this.isVisible = true
		},
		onHide () {
			this.blurActiveTooltip()
			this.isVisible = false
		}
	}
}
</script>
<style lang="scss">
/* stylelint-disable selector-class-pattern */
// Default tooltip theme
.v-popper--theme-tooltip {
    display: inline-block;

    .v-popper__inner {
        background: getColor(tooltip-bg);
        color: getColor(white);
        border-radius: 4px;
        padding: 10px;
        font-size: 12px;
        line-height: 14px;
        box-shadow: 0 2px 4px getColor(shadow);
        word-wrap: break-word;
        overflow-wrap: break-word;
        white-space: pre-line;
        max-width: 300px;
    }

    .v-popper__arrow-inner {
        visibility: hidden;
    }

    .v-popper__arrow-outer {
        border-color: getColor(tooltip-bg);
    }
}

// White tooltip variant (inline inverse surface)
.v-popper__popper.white-tooltip {
    .v-popper__inner {
        background: getColor(white);
        color: getColor(dark-text);
        box-shadow: 0 2px 4px getColor(shadow);
    }

    .v-popper__arrow-outer {
        border-color: getColor(white);
    }

    .v-popper__arrow-inner {
        border-color: getColor(white);
        visibility: visible;
    }
}

// Wrap modifier
.v-popper__popper.tooltip-wrap {
    .v-popper__inner {
        min-width: 250px;
        max-width: 300px;
        white-space: normal;
    }
}

// Ensure tooltip appears above modals
.v-popper__popper {
    z-index: 19999 !important;
}
/* stylelint-enable selector-class-pattern */
</style>
